export class MsalAuthenticatedApiGateway {
    constructor(name, msalAuthenticationContext, apiBaseUrl, apiScopes) {
        Object.defineProperty(this, "name", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "msalAuthenticationContext", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "apiBaseUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "apiScopes", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "cachedApiAccessTokenAuthenticationResult", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "fetch", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (functionName, payload) => {
                // Add W3CTraceContext and any other properties common to all requests
                payload.TraceContext = {
                    TraceId: crypto.randomUUID().replace("-", ""),
                    ParentId: null,
                    ParentActorId: null,
                    ParentSystemVersion: null,
                    TraceSystemVersion: null
                };
                const payloadJson = JSON.stringify(payload);
                const response = await this.fetchAndAcquireAccessTokenIfNecessary(functionName, payloadJson);
                const result = response.json();
                return result;
            }
        });
        Object.defineProperty(this, "fetchResponse", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (functionName, payloadString) => {
                const response = await this.fetchAndAcquireAccessTokenIfNecessary(functionName, payloadString);
                const result = this.createResponseDtoFromResponse(response);
                return result;
            }
        });
        Object.defineProperty(this, "createResponseDtoFromResponse", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (response) => {
                const result = {
                    Status: response.status,
                    StatusText: response.statusText,
                    ResponseContent: await response.text()
                };
                return result;
            }
        });
        Object.defineProperty(this, "getAccessToken", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                const now = new Date(Date.now());
                if (this.cachedApiAccessTokenAuthenticationResult) {
                    if (this.cachedApiAccessTokenAuthenticationResult.expiresOn && now >= this.cachedApiAccessTokenAuthenticationResult.expiresOn) {
                        // MSAL.js: acquire a token to call an API-https://learn.microsoft.com/en-us/entra/identity-platform/scenario-spa-acquire-token?tabs=javascript2
                        console.log("An access token for API ", this.name, " has already been acquired but has now expired (expiresOn=", this.cachedApiAccessTokenAuthenticationResult.expiresOn, ", now=", now, ". Acquiring a new access token now...");
                        const result = await this.acquireAccessTokenBypassingCache();
                        return result;
                    }
                    else {
                        return this.cachedApiAccessTokenAuthenticationResult;
                    }
                }
                else {
                    console.log("An access token for API ", this.name, " has not been acquired yet. Acquiring for the first time now...");
                    const result = await this.acquireAccessTokenBypassingCache();
                    return result;
                }
            }
        });
        Object.defineProperty(this, "acquireAccessTokenBypassingCache", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                const accessTokenAuthenticationResult = await this.msalAuthenticationContext.acquireTokenForActiveAccountSilentlyOrWithPopupIfInteractionRequired(this.apiScopes);
                if (accessTokenAuthenticationResult) {
                    this.cachedApiAccessTokenAuthenticationResult = accessTokenAuthenticationResult;
                    console.log("API ", this.name, " has used acquireTokenSilent to acquire a new access token");
                    return this.cachedApiAccessTokenAuthenticationResult;
                }
                else {
                    throw "Failed to obtain token. Either the user does not have permissions or they must log in again.";
                }
            }
        });
        Object.defineProperty(this, "fetchAndAcquireAccessTokenIfNecessary", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (functionName, payloadString) => {
                let accessTokenAuthorizationResult = await this.getAccessToken();
                let accessToken = accessTokenAuthorizationResult.accessToken;
                let options = this.buildOptions(payloadString, accessToken);
                const url = `${this.apiBaseUrl}${functionName}`;
                const performanceStartNow = Date.now();
                console.log(`Fetching ${url}`, performanceStartNow);
                let response = await fetch(url, options);
                if (response.status == 401 || response.status == 403) {
                    console.warn("First attemp to fetch from URL '", url, "' with access token '", accessToken, ", failed with StatusCode='", response.status, "', StatusText='", response.statusText, "'. Trying again with a new access token.");
                    accessTokenAuthorizationResult = await this.acquireAccessTokenBypassingCache();
                    accessToken = accessTokenAuthorizationResult.accessToken;
                    options = this.buildOptions(payloadString, accessToken);
                    response = await fetch(url, options);
                    if (response.status >= 200 && response.status < 300) {
                        const result = response;
                        return result;
                    }
                    else {
                        throw `Failed to fetch from URL '${url}' and access token ${accessToken}. StatusCode='${response.status}', StatusText='${response.statusText}'`;
                    }
                }
                else {
                    const result = await response;
                    const performanceEndNow = Date.now();
                    // Uncomment the following line to force a delay so that it's easy to test what happens when the network requests take a while
                    //await new Promise(r => setTimeout(r, 10000));
                    console.log(`Fetched ${url}`, performanceEndNow, "ellapsed time", (performanceEndNow - performanceStartNow).toLocaleString(), "milliseconds");
                    return result;
                }
            }
        });
        Object.defineProperty(this, "buildOptions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (payloadString, accessToken) => {
                const result = {
                    method: "POST",
                    body: payloadString,
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": "text/plain; charset=utf-8"
                    }
                };
                return result;
            }
        });
        this.name = name;
        this.msalAuthenticationContext = msalAuthenticationContext;
        this.apiBaseUrl = apiBaseUrl.endsWith('/') ? apiBaseUrl : (apiBaseUrl + "/");
        this.apiScopes = apiScopes;
    }
}
